import React from 'react';
import AltIso from 'alt/utils/AltIso';
import {Link} from 'react-router';
import moment from 'moment';
import _ from 'lodash';
import connectToStores from 'alt/utils/connectToStores';
import withRouter from '../utils/with-router';
import doRedirect from '../utils/doRedirect';
import reportTime from '../utils/reportTime';

import DetailsShiftModal from '../components/DetailsShiftModal';
import DashboardHeader from '../components/DashboardHeader';
import Calendar from '../components/Calendar/Calendar';
import AgendaHeader from '../components/AgendaHeader';

import SeoActions from '../actions/SeoActions';

import CurrentUserStore from '../stores/CurrentUserStore';
import ShiftStore from '../stores/ShiftStore';
import ChatStore from '../stores/ChatStore';
import ChatMessageIsReadStore from '../stores/ChatMessageIsReadStore';

@AltIso.define((props) => {
    return Promise.all([
        new Promise((resolve) => {
            SeoActions.pushTitle('My Shifts');
            resolve();
        }),
        ShiftStore.getMyShifts(moment()),
        CurrentUserStore.getState()
    ]);
})
@connectToStores
@withRouter
class MyShifts extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            selected: moment().startOf("day"),
            selecting: false,
            month: moment().startOf("day"),
            showAgendaHeader: false,
            showTopDummy: false,
            agendaHeaderPos: 0,
            agendaHeaderTitle: moment().startOf("day").format('dddd D'),
            selectedTab: "agenda",
            showCalendar: true,
            showShiftModal: false,
            isLoading: false,
            messageSent: false
        };
    }

    static getPropsFromStores() {
        return {
            shiftStore: ShiftStore.getState(),
            chatStore: ChatStore.getState(),
            chatMessageIsReadStore: ChatMessageIsReadStore.getState()
        };
    }

    static getStores() {
        return [ShiftStore, ChatStore, ChatMessageIsReadStore];
    }

    dom(ref) {
        // Helper function to wrap getDOMNode
        return React.findDOMNode(this.refs[ref]);
    }

    getActiveDayStart() {
        return moment(_.result(_.find(this.props.shiftStore.myShifts, item => {
                return moment(item.start_time) >= this.state.selected;
            }), 'start_time')).startOf('day');
    }

    getNextDayStart() {
        return moment(_.result(_.find(this.props.shiftStore.myShifts, item => {
                return moment(item.start_time) > this.getActiveDayStart().endOf('day');
            }), 'start_time')).startOf('day');
    }

    getPrevDayStart() {
        const prevStart = _.result(_.findLast(this.props.shiftStore.myShifts, item => {
                return moment(item.start_time) < this.state.selected;
            }), 'start_time');
        return typeof prevStart === "undefined" ? false : moment(prevStart).startOf('day');
    }

    resetScroll() {
        this.dom("invisiscroller").scrollTop = this.dom('topdummy').scrollHeight;
    }

    handleAgendaHeader(evt) {
        if (!this.state.selecting && this.dom('topdummy') && evt.target === this.dom("invisiscroller")) {
            const currentRef = this.getActiveDayStart().format('dddd D'),
                offsetTop = this.dom('topdummy').scrollHeight - this.dom("invisiscroller").scrollTop, // If this is > 0, you scrolled UP
                offsetBottom = this.dom(currentRef).scrollHeight - this.dom("invisiscroller").scrollTop + this.dom('topdummy').scrollHeight; // If this is < 0, you scrolled PAST THE FIRST ELEMENT
            if (offsetBottom < 0) {
                const d = this.getNextDayStart();
                this.setState({
                    showTopDummy: true,
                    selected: d,
                    agendaHeaderTitle: d.format('dddd D')
                });
                this.resetScroll();
            } else if (offsetTop > 0) {
                const d = this.getPrevDayStart();
                if (!d) {this.setState({showTopDummy: false}); return;} // Don't scroll past very first shift this month
                this.setState({
                    selected: d,
                    agendaHeaderTitle: d.format('dddd D')
                });
                const tempRef = this.getActiveDayStart().format('dddd D');
                this.dom("invisiscroller").scrollTop += this.dom(tempRef).scrollHeight;
                this.setState({showAgendaHeader: false});
            } else {
                if (offsetTop !== 0) {this.setState({showAgendaHeader: true});} // Don't show it on load or standstill
            }
        }
        setTimeout((x) => {this.setState({selecting: false})}, 150);
    }

    componentDidMount() {
        // Scroll it down by dummy length (Changed to 1px to prevent scrolling, R.I.P my cool scrolling animations thx Ivar)
        if (this.props.shiftStore.myShifts.length !== 0) {
            React.findDOMNode(this.refs['invisiscroller']).scrollTop = 1;

            this._handleAgendaHeader = this.handleAgendaHeader.bind(this);
            window.addEventListener('scroll', this._handleAgendaHeader, true);
        }
        if (!this.props.user.profile.profile.is_candidate) {
            setTimeout((x) => {doRedirect(this.context.router, 'completeprofile')}, 50);
        }
    }

    componentWillReceiveProps(nextProps) {
        if (this.state.activeShift && nextProps.chatStore.messageSent !== this.state.messageSent) {
            //Setting timeout to give spinner a chance to show properly
            const shiftId = this.state.activeShift.id;
            setTimeout(x => {this.setState({messageSent: nextProps.chatStore.messageSent, isLoading: false})}, 250);
            setTimeout(x => {ChatStore.getMessages({shift: shiftId, user: this.props.user.profile.profile.id})}, 1500);
        }
    }

    componentWillUnmount() {
        window.removeEventListener('scroll', this._handleAgendaHeader);
    }

    mapShifts(list) {
        let days = {},
            results = [];
        // First let's group events by day
        list.forEach(shift => {
            const start_time = moment(shift.start_time).utcOffset(shift.start_time),
                end_time = moment(shift.end_time).utcOffset(shift.start_time),
                date = moment(shift.start_time).startOf("day"),
                message_count = shift.message_count;

            // Don't render if it's before the selected date
            if (date < this.state.selected) {
                return;
            }

            const displayed_date = start_time.format('dddd D'),
                isCompleted = end_time < moment(),
                isFinished = shift.status == "finished",
                isCancelled = shift.status == "cancelled" || (shift.hiring && ['cancelled', 'cancelled by us'].indexOf(shift.hiring.status) >= 0);
            let note, titleStyles = "shift-title dot ";

            // Note
            note = isCancelled ? <span className="note">Cancelled</span> : isFinished ? <span className="note">Completed</span> :
                isCompleted ? <span className="note">To be finished</span> : <span className="note">Approved</span>;

            // Dot color
            titleStyles += isFinished || isCancelled ? "dot-green" : "dot-orange";

            // Hours and pay
            let hours_pay = null;
            // We only show the hours and pay if the shift has been finished.
            // This also includes the cancelled shifts and PROs that where declined after being hired.
            if (isFinished) {
                hours_pay = `| ${shift.total_hours}h | ${DJ_CONST.LOCALE_CONF.currency}${shift.total_pay}`;
            }

            let shiftBlock = (
                <a onClick={this.toggleShiftModal.bind(this, shift)} className="shift" key={shift.id}>
                    <h4 className={titleStyles}>{shift.position ? shift.position.name : ''} {shift.position ? '-' : ''} {shift.venue} {note}</h4>

                    <div className="shift-info">
                        {reportTime(start_time, end_time)} | {shift.company.name}
                        {hours_pay}
                    </div>
                    {(message_count > 0 && !(isCompleted && !isFinished)? <div className="shift-circle"><span>{message_count}</span></div> : '')}
                    <div className="shift-arrow"></div>
                </a>
            );
            if (days[displayed_date]) {
                days[displayed_date].push(shiftBlock);
            } else {
                days[displayed_date] = [shiftBlock]
            }
        });

        // And now output the daily list
        if (Object.keys(days).length > 0) {
            for (const date in days) {
                if (days.hasOwnProperty(date)) {
                    results.push(
                        <div ref={date} key={`d${date}`} className="agenda-day">
                            <h3 className="day-title">{date}</h3>
                            {days[date]}
                        </div>
                    );
                }
            }
        } else {
            results.push(
                <div ref="emptyAgenda" className="empty-agenda">
                        <div className="empty-info">
                            <div className="map"></div>
                            <div className="empty-title">You have gone too far!</div>
                            <p>
                                It appears that there are no shifts for which you have been hired after the selected date.
                                View available offers?
                            </p>
                            <Link to="offers" className="btn btn-green btn-new-shift">View offers</Link>
                        </div>
                </div>
            )
        }

        return results;
    }

    toggleShiftModal(activeShift) {
        if (activeShift) {
            ChatStore.getMessages({'shift': activeShift.id, 'user': this.props.user.profile.profile.id});
        }
        this.setState({showShiftModal: !this.state.showShiftModal, activeShift});
    }

    sendMessage(data) {
        this.setState({isLoading: true});
        ChatStore.sendMessage(data);
    }

    makeMessagesRead() {
        const shift = this.state.activeShift;
        if (shift.hiring) {
            ChatMessageIsReadStore.create(shift.message_count, shift.hiring.id);
        }
        shift.message_count = 0;
    }

    getSelection(date) {
        this.resetScroll();
        this.setState({selected: date, selecting: true, showAgendaHeader: false, agendaHeaderTitle: moment(date).format('dddd D')});
    }

    monthChangeCallback(date) {
        ShiftStore.getMyShifts(date);
    }

    changeTab(selectedTab) {
        this.setState({selectedTab});
    }

    prevMonth() {
        const newMonth = this.state.month.add(-1, "M");
        this.monthChangeCallback(newMonth);
        this.setState({
            month: newMonth,
            showTopDummy: false,
            showAgendaHeader: false
        });
    }

    nextMonth() {
        const newMonth = this.state.month.add(1, "M");
        this.monthChangeCallback(newMonth);
        this.setState({
            month: newMonth,
            showTopDummy: false,
            showAgendaHeader: false
        });
    }

    select(day) {
        const state = {};
        if (day.isNextMonth) {
            state.month = this.state.month.add(1, "M");
            this.monthChangeCallback.call(this, state['month']);
        } else if (day.isPrevMonth) {
            state.month = this.state.month.add(-1, "M");
            this.monthChangeCallback.call(this, state['month']);
        }
        this.getSelection.call(this, day.date);
        this.setState(state);
        this.forceUpdate();
    }

    calendarToggle() {
        this.setState({
            showCalendar: !this.state.showCalendar
        });
    }

    render() {
        const shifts = this.props.shiftStore.myShifts;
        const showShiftModal = this.state.showShiftModal;
        const activeShift = this.state.activeShift;
        const chatMessages = this.props.chatStore.messages || [];
        const messageSent = this.state.messageSent;
        const isLoading = this.state.isLoading;

        return (
            <div className="view-dashboard-overview">
                <DashboardHeader isPro={this.props.isPro()} changeTab={this.changeTab.bind(this)} selectedTab={this.state.selectedTab} />

                <div className="view-content maxwidth-wrapper">
                    <div className="media">
                        <Calendar month={this.state.month} shifts={shifts}
                                  prevMonth={this.prevMonth.bind(this)}
                                  nextMonth={this.nextMonth.bind(this)}
                                  select={this.select.bind(this)}
                                  selected={this.state.selected}
                                  iconClick={this.calendarToggle.bind(this)}
                                  className={this.state.showCalendar ? '' : 'calendar-hidden'}
                            />

                        <div className="agenda-week body">
                            <AgendaHeader
                                display={this.state.showAgendaHeader}
                                title={this.state.agendaHeaderTitle}
                                />
                            <div ref="invisiscroller" className="invisi-scroll">
                                <div ref="whitebox" className="whitebox">
                                    <div ref="topdummy" className={`agenda-dummy${this.state.showTopDummy ? "" : " hide"}`}></div>
                                    {this.mapShifts(shifts)}
                                    <div ref="botdummy" className={`agenda-dummy bottom`}></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <DetailsShiftModal toggleModal={this.toggleShiftModal.bind(this)}
                    isVisible={showShiftModal} shift={activeShift}
                    messages={chatMessages}
                    isLoading={isLoading}
                    messageSent={messageSent}
                    user={this.props.user.profile}
                    sendMessage={this.sendMessage.bind(this)}
                    makeMessagesRead={this.makeMessagesRead.bind(this)} />
            </div>
        );
    }
}
export default MyShifts;



/** WEBPACK FOOTER **
 ** ./app/src/views/MyShifts.js
 **/